import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api, getUser, formatDate } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload, { GalleryUpload } from "@/components/ImageUpload";
import JobsOwnerPanel from "@/components/JobsOwnerPanel";
import { Building2, GraduationCap, ImagePlus, Send, Clock, Check, X, Users, ExternalLink, ShieldCheck } from "lucide-react";

const emptyInstitution = {
  name: "", type: "school", logo: "", banner: "",
  description: "", established_year: null, affiliation: "", board: "", principal: "",
  state: "", city: "", address: "", phone: "", email: "", website: "",
  courses: [], facilities: [], gallery: [], achievements: [],
};

const emptySupplier = {
  name: "", category: "EdTech", logo: "", banner: "", description: "",
  products: [], services: [], website: "", email: "", phone: "", city: "", state: "",
};

export default function AccountDashboard() {
  const user = getUser();
  if (!user) return <Navigate to="/login" />;

  return (
    <div className="max-w-6xl mx-auto px-4 py-10" data-testid="account-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <div className="overline text-sky-700 mb-1">Your Space</div>
          <h1 className="headline text-3xl md:text-4xl font-bold text-slate-900">Welcome, {user.name}</h1>
          <p className="text-sm text-slate-500 mt-1 capitalize">Role: {user.role}</p>
        </div>
        {(user.role === "admin" || user.role === "editor") && (
          <Link to="/admin" className="px-4 py-2 rounded-full bg-sky-700 text-white text-sm font-semibold">Go to Editorial Console →</Link>
        )}
      </div>

      {user.role === "institution" && <InstitutionOwnerPanel />}
      {user.role === "supplier" && <SupplierOwnerPanel />}
      {user.role === "reader" && <ReaderPanel />}
    </div>
  );
}

function ReaderPanel() {
  return (
    <div className="card-soft p-8 text-center">
      <h2 className="font-editorial text-2xl font-bold mb-2">You're on the reader plan.</h2>
      <p className="text-slate-600 mb-6">Upgrade your account to publish news, manage a school/college profile, or list your education company.</p>
      <div className="grid sm:grid-cols-2 gap-4 max-w-lg mx-auto">
        <Link to="/register" className="card-soft p-5 text-left hover-zoom">
          <GraduationCap className="w-6 h-6 text-sky-700 mb-2"/>
          <div className="font-editorial font-bold text-slate-900">Institution</div>
          <p className="text-xs text-slate-500 mt-1">School, college, or university admin.</p>
        </Link>
        <Link to="/register" className="card-soft p-5 text-left hover-zoom">
          <Building2 className="w-6 h-6 text-emerald-700 mb-2"/>
          <div className="font-editorial font-bold text-slate-900">Supplier</div>
          <p className="text-xs text-slate-500 mt-1">EdTech, ERP, furniture, publisher.</p>
        </Link>
      </div>
    </div>
  );
}

function InstitutionOwnerPanel() {
  const [inst, setInst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("profile");
  const [submissions, setSubmissions] = useState([]);
  const [leads, setLeads] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/my/institution");
      setInst(r.data);
    } catch { setInst(null); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!inst) return;
    api.get("/my/institution/submissions").then(r => setSubmissions(r.data));
    api.get("/my/institution/leads").then(r => setLeads(r.data));
  }, [inst, tab]);

  if (loading) return <div className="text-slate-500">Loading…</div>;

  if (!inst) return <CreateInstitutionForm onCreated={load} />;

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="institution-tabs">
        {[
          { k: "profile", l: "Profile" },
          { k: "news", l: "Submit News" },
          { k: "submissions", l: `Submissions${submissions.length ? ` (${submissions.length})` : ""}` },
          { k: "jobs", l: "Jobs" },
          { k: "leads", l: `Leads${leads.length ? ` (${leads.length})` : ""}` },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === t.k ? "bg-sky-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-sky-100"}`} data-testid={`inst-tab-${t.k}`}>{t.l}</button>
        ))}
        <Link to={`/institution/${inst.slug}`} target="_blank" className="ml-auto px-4 py-2 rounded-full bg-slate-100 text-sm text-slate-700 flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5"/>View public page</Link>
      </div>

      {tab === "profile" && <EditInstitutionForm inst={inst} onSaved={load} />}
      {tab === "news" && <SubmitNewsForm onSubmitted={() => { setTab("submissions"); load(); }} />}
      {tab === "submissions" && <SubmissionsList items={submissions} />}
      {tab === "jobs" && <JobsOwnerPanel />}
      {tab === "leads" && <LeadsList items={leads} />}
    </div>
  );
}

function CreateInstitutionForm({ onCreated }) {
  const [f, setF] = useState(emptyInstitution);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...f, established_year: f.established_year ? Number(f.established_year) : null };
      await api.post("/my/institution", payload);
      toast.success("Profile created — pending verification");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="card-soft p-8 space-y-4" data-testid="create-institution-form">
      <div className="flex items-center gap-2 text-sky-700 mb-2"><ShieldCheck className="w-4 h-4"/><span className="overline">Create your institution profile</span></div>
      <p className="text-sm text-slate-600 mb-4">Fill in the basics — you can edit anytime. Editors will verify your profile within 24 hours.</p>
      <input required placeholder="Institution name" value={f.name} onChange={e => setF({...f, name: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-lg" data-testid="inst-name-input"/>
      <div className="grid md:grid-cols-3 gap-3">
        <select value={f.type} onChange={e => setF({...f, type: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-sky-400">
          <option value="school">School</option>
          <option value="college">College</option>
          <option value="university">University</option>
        </select>
        <input placeholder="Established year" type="number" value={f.established_year || ""} onChange={e => setF({...f, established_year: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="Affiliation (CBSE / UGC / ...)" value={f.affiliation} onChange={e => setF({...f, affiliation: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
      </div>
      <textarea rows={4} placeholder="About your institution" value={f.description} onChange={e => setF({...f, description: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
      <div className="grid md:grid-cols-2 gap-3">
        <ImageUpload label="Banner image" value={f.banner} onChange={(v) => setF({...f, banner: v})} testId="inst-banner" height="h-40"/>
        <ImageUpload label="Logo" value={f.logo} onChange={(v) => setF({...f, logo: v})} testId="inst-logo" height="h-40"/>
        <input placeholder="City" value={f.city} onChange={e => setF({...f, city: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="State" value={f.state} onChange={e => setF({...f, state: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="Email" value={f.email} onChange={e => setF({...f, email: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="Phone" value={f.phone} onChange={e => setF({...f, phone: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="Website" value={f.website} onChange={e => setF({...f, website: e.target.value})} className="md:col-span-2 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
      </div>
      <button disabled={saving} className="px-6 py-3 rounded-full bg-sky-700 text-white font-semibold disabled:opacity-60" data-testid="inst-create-btn">{saving ? "Creating…" : "Create Profile"}</button>
    </form>
  );
}

function EditInstitutionForm({ inst, onSaved }) {
  const [f, setF] = useState({ ...emptyInstitution, ...inst });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...f,
        established_year: f.established_year ? Number(f.established_year) : null,
        courses: Array.isArray(f.courses) ? f.courses : (f.courses || "").split(",").map(x => x.trim()).filter(Boolean),
        facilities: Array.isArray(f.facilities) ? f.facilities : (f.facilities || "").split(",").map(x => x.trim()).filter(Boolean),
        achievements: Array.isArray(f.achievements) ? f.achievements : (f.achievements || "").split(",").map(x => x.trim()).filter(Boolean),
        gallery: Array.isArray(f.gallery) ? f.gallery : (f.gallery || "").split(",").map(x => x.trim()).filter(Boolean),
      };
      await api.put("/my/institution", payload);
      toast.success("Profile updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="card-soft p-8 space-y-4" data-testid="edit-institution-form">
      <div className="flex items-center gap-2">
        <h3 className="font-editorial text-xl font-bold text-slate-900">Edit your profile</h3>
        {inst.verified ? <span className="text-[10px] overline px-2 py-1 rounded-full tag-green">Verified</span> : <span className="text-[10px] overline px-2 py-1 rounded-full bg-amber-100 text-amber-800">Pending review</span>}
      </div>
      <input required placeholder="Name" value={f.name} onChange={e => setF({...f, name: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-lg"/>
      <div className="grid md:grid-cols-3 gap-3">
        <select value={f.type} onChange={e => setF({...f, type: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-sky-400">
          <option value="school">School</option><option value="college">College</option><option value="university">University</option>
        </select>
        <input placeholder="Established year" type="number" value={f.established_year || ""} onChange={e => setF({...f, established_year: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="Affiliation" value={f.affiliation || ""} onChange={e => setF({...f, affiliation: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
      </div>
      <textarea rows={5} placeholder="About" value={f.description || ""} onChange={e => setF({...f, description: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
      <div className="grid md:grid-cols-2 gap-3">
        <ImageUpload label="Banner" value={f.banner} onChange={(v) => setF({...f, banner: v})} testId="inst-edit-banner" height="h-40"/>
        <ImageUpload label="Logo" value={f.logo} onChange={(v) => setF({...f, logo: v})} testId="inst-edit-logo" height="h-40"/>
        <input placeholder="Principal" value={f.principal || ""} onChange={e => setF({...f, principal: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="Board" value={f.board || ""} onChange={e => setF({...f, board: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="City" value={f.city || ""} onChange={e => setF({...f, city: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="State" value={f.state || ""} onChange={e => setF({...f, state: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="Email" value={f.email || ""} onChange={e => setF({...f, email: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="Phone" value={f.phone || ""} onChange={e => setF({...f, phone: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="Website" value={f.website || ""} onChange={e => setF({...f, website: e.target.value})} className="md:col-span-2 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="Courses (comma separated)" value={Array.isArray(f.courses) ? f.courses.join(", ") : f.courses} onChange={e => setF({...f, courses: e.target.value})} className="md:col-span-2 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="Facilities (comma separated)" value={Array.isArray(f.facilities) ? f.facilities.join(", ") : f.facilities} onChange={e => setF({...f, facilities: e.target.value})} className="md:col-span-2 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <input placeholder="Achievements (comma separated)" value={Array.isArray(f.achievements) ? f.achievements.join(", ") : f.achievements} onChange={e => setF({...f, achievements: e.target.value})} className="md:col-span-2 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        <div className="md:col-span-2">
          <GalleryUpload label="Gallery" value={f.gallery} onChange={(v) => setF({...f, gallery: v})} testId="inst-gallery"/>
        </div>
      </div>
      <button disabled={saving} className="px-6 py-3 rounded-full bg-sky-700 text-white font-semibold disabled:opacity-60" data-testid="inst-save-btn">{saving ? "Saving…" : "Save Changes"}</button>
    </form>
  );
}

function SubmitNewsForm({ onSubmitted }) {
  const [f, setF] = useState({ title: "", subtitle: "", content: "", featured_image: "", tags: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/my/institution/news", { ...f, tags: f.tags.split(",").map(t => t.trim()).filter(Boolean) });
      toast.success("Submitted for editorial review");
      setF({ title: "", subtitle: "", content: "", featured_image: "", tags: "" });
      onSubmitted?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="card-soft p-8 space-y-4" data-testid="submit-news-form">
      <div className="flex items-center gap-2 text-sky-700 mb-2"><Send className="w-4 h-4"/><span className="overline">Submit press release / news</span></div>
      <p className="text-sm text-slate-600 mb-2">Your submission will appear on your institution page and (once approved by editors) on the EduTime homepage under Press Releases.</p>
      <input required placeholder="Headline" value={f.title} onChange={e => setF({...f, title: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-lg" data-testid="news-title"/>
      <input placeholder="Subtitle / one-line summary" value={f.subtitle} onChange={e => setF({...f, subtitle: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
      <ImageUpload label="Featured image (optional)" value={f.featured_image} onChange={(v) => setF({...f, featured_image: v})} testId="news-hero" height="h-40"/>
      <textarea required rows={10} placeholder="Full content (HTML allowed)" value={f.content} onChange={e => setF({...f, content: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-mono text-sm" data-testid="news-content"/>
      <input placeholder="Tags (comma separated)" value={f.tags} onChange={e => setF({...f, tags: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
      <button disabled={saving} className="px-6 py-3 rounded-full bg-sky-700 text-white font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="news-submit-btn"><Send className="w-4 h-4"/>{saving ? "Submitting…" : "Submit for Review"}</button>
    </form>
  );
}

function SubmissionsList({ items }) {
  if (!items.length) return <div className="card-soft p-8 text-center text-slate-500">No submissions yet.</div>;
  const badge = { pending: "bg-amber-100 text-amber-800", approved: "tag-green", rejected: "bg-red-100 text-red-700" };
  const icon = { pending: Clock, approved: Check, rejected: X };
  return (
    <div className="space-y-3">
      {items.map(s => {
        const Ic = icon[s.status] || Clock;
        return (
          <div key={s.id} className="card-soft p-5 flex items-start gap-4">
            <div className={`w-9 h-9 rounded-xl grid place-items-center ${s.status === "pending" ? "bg-amber-100 text-amber-700" : s.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}><Ic className="w-4 h-4"/></div>
            <div className="flex-1">
              <h4 className="font-editorial font-bold text-slate-900">{s.title}</h4>
              <p className="text-xs text-slate-500 mt-1">Submitted {formatDate(s.created_at)}</p>
            </div>
            <span className={`overline text-[10px] px-2.5 py-1 rounded-full ${badge[s.status]}`}>{s.status}</span>
          </div>
        );
      })}
    </div>
  );
}

function LeadsList({ items }) {
  if (!items.length) return <div className="card-soft p-8 text-center text-slate-500" data-testid="leads-empty">No enquiries yet. Prospects who fill out the lead form on your public page will show up here.</div>;
  return (
    <div className="card-soft overflow-hidden" data-testid="leads-list">
      <table className="w-full text-sm">
        <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
          <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Phone</th><th className="px-5 py-3">Message</th><th className="px-5 py-3">Received</th></tr>
        </thead>
        <tbody>
          {items.map(l => (
            <tr key={l.id} className="border-b border-slate-50">
              <td className="px-5 py-3 font-editorial font-semibold text-slate-900">{l.name}</td>
              <td className="px-5 py-3 text-slate-700"><a href={`mailto:${l.email}`} className="text-sky-700">{l.email}</a></td>
              <td className="px-5 py-3 text-slate-700">{l.phone || "—"}</td>
              <td className="px-5 py-3 text-slate-600 max-w-md">{l.message}</td>
              <td className="px-5 py-3 text-xs text-slate-500">{formatDate(l.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Supplier owner ----------
function SupplierOwnerPanel() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("profile");
  const [leads, setLeads] = useState([]);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/my/supplier"); setS(r.data); }
    catch { setS(null); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (s) api.get("/my/supplier/leads").then(r => setLeads(r.data)); }, [s, tab]);

  if (loading) return <div className="text-slate-500">Loading…</div>;
  if (!s) return <CreateSupplierForm onCreated={load} />;

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="supplier-tabs">
        {[
          { k: "profile", l: "Profile" },
          { k: "jobs", l: "Jobs" },
          { k: "leads", l: `Leads${leads.length ? ` (${leads.length})` : ""}` },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === t.k ? "bg-emerald-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-emerald-100"}`} data-testid={`sup-tab-${t.k}`}>{t.l}</button>
        ))}
        <Link to={`/supplier/${s.slug}`} target="_blank" className="ml-auto px-4 py-2 rounded-full bg-slate-100 text-sm text-slate-700 flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5"/>View public page</Link>
      </div>
      {tab === "profile" && <EditSupplierForm sup={s} onSaved={load} />}
      {tab === "jobs" && <JobsOwnerPanel />}
      {tab === "leads" && <LeadsList items={leads} />}
    </div>
  );
}

function CreateSupplierForm({ onCreated }) {
  const [f, setF] = useState(emptySupplier);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post("/my/supplier", f); toast.success("Supplier profile created"); onCreated(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="card-soft p-8 space-y-4" data-testid="create-supplier-form">
      <div className="flex items-center gap-2 text-emerald-700 mb-2"><ShieldCheck className="w-4 h-4"/><span className="overline">Create your supplier listing</span></div>
      <input required placeholder="Company name" value={f.name} onChange={e => setF({...f, name: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400 font-editorial text-lg" data-testid="sup-name-input"/>
      <select value={f.category} onChange={e => setF({...f, category: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-emerald-400">
        {["EdTech","ERP","Furniture","Smart Class","Laboratory Equipment","Publishers","Uniform","Transport","Architecture","Campus Technology","Recruitment","Learning Platforms"].map(c => <option key={c}>{c}</option>)}
      </select>
      <textarea rows={4} placeholder="Description" value={f.description} onChange={e => setF({...f, description: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400"/>
      <ImageUpload label="Banner" value={f.banner} onChange={(v) => setF({...f, banner: v})} testId="sup-banner" height="h-40"/>
      <div className="grid md:grid-cols-2 gap-3">
        <input placeholder="Website" value={f.website} onChange={e => setF({...f, website: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400"/>
        <input placeholder="Email" value={f.email} onChange={e => setF({...f, email: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400"/>
        <input placeholder="Phone" value={f.phone} onChange={e => setF({...f, phone: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400"/>
        <input placeholder="City" value={f.city} onChange={e => setF({...f, city: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400"/>
        <input placeholder="State" value={f.state} onChange={e => setF({...f, state: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400"/>
      </div>
      <button disabled={saving} className="px-6 py-3 rounded-full bg-emerald-700 text-white font-semibold disabled:opacity-60" data-testid="sup-create-btn">{saving ? "Creating…" : "Create Listing"}</button>
    </form>
  );
}

function EditSupplierForm({ sup, onSaved }) {
  const [f, setF] = useState({ ...emptySupplier, ...sup });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...f,
        products: Array.isArray(f.products) ? f.products : (f.products || "").split(",").map(x => x.trim()).filter(Boolean),
        services: Array.isArray(f.services) ? f.services : (f.services || "").split(",").map(x => x.trim()).filter(Boolean),
      };
      await api.put("/my/supplier", payload);
      toast.success("Listing updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="card-soft p-8 space-y-4" data-testid="edit-supplier-form">
      <div className="flex items-center gap-2">
        <h3 className="font-editorial text-xl font-bold text-slate-900">Edit your listing</h3>
        {sup.verified ? <span className="text-[10px] overline px-2 py-1 rounded-full tag-green">Verified</span> : <span className="text-[10px] overline px-2 py-1 rounded-full bg-amber-100 text-amber-800">Pending review</span>}
      </div>
      <input required placeholder="Name" value={f.name} onChange={e => setF({...f, name: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400 font-editorial text-lg"/>
      <select value={f.category} onChange={e => setF({...f, category: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-emerald-400">
        {["EdTech","ERP","Furniture","Smart Class","Laboratory Equipment","Publishers","Uniform","Transport","Architecture","Campus Technology","Recruitment","Learning Platforms"].map(c => <option key={c}>{c}</option>)}
      </select>
      <textarea rows={4} placeholder="Description" value={f.description || ""} onChange={e => setF({...f, description: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400"/>
      <ImageUpload label="Banner" value={f.banner} onChange={(v) => setF({...f, banner: v})} testId="sup-edit-banner" height="h-40"/>
      <div className="grid md:grid-cols-2 gap-3">
        <input placeholder="Website" value={f.website || ""} onChange={e => setF({...f, website: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400"/>
        <input placeholder="Email" value={f.email || ""} onChange={e => setF({...f, email: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400"/>
        <input placeholder="Phone" value={f.phone || ""} onChange={e => setF({...f, phone: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400"/>
        <input placeholder="City" value={f.city || ""} onChange={e => setF({...f, city: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400"/>
        <input placeholder="State" value={f.state || ""} onChange={e => setF({...f, state: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400"/>
        <input placeholder="Products (comma separated)" value={Array.isArray(f.products) ? f.products.join(", ") : f.products} onChange={e => setF({...f, products: e.target.value})} className="md:col-span-2 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400"/>
        <input placeholder="Services (comma separated)" value={Array.isArray(f.services) ? f.services.join(", ") : f.services} onChange={e => setF({...f, services: e.target.value})} className="md:col-span-2 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-emerald-400"/>
      </div>
      <button disabled={saving} className="px-6 py-3 rounded-full bg-emerald-700 text-white font-semibold disabled:opacity-60" data-testid="sup-save-btn">{saving ? "Saving…" : "Save Changes"}</button>
    </form>
  );
}
